import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslateService } from '@ngx-translate/core';
import { isEqual } from 'lodash';
import { BehaviorSubject, Observable, map, shareReplay, take, tap } from 'rxjs';
import { AccessControl } from '@pncl/common-models';
import { FeatureManagementService } from 'src/app/services/feature-management.service';
import { CapabilityTierService } from 'src/app/services/capability-tier.service';
import { IconTypes } from 'src/app/standalones/components/pencil-icon/pencil-icon.component';
import { startWith } from 'rxjs/operators';
import {
  DEFAULT_SHARE_SNIPPETS_TRIGGER,
  EmailAudience,
  SnippetsNotificationAndVisibilityTrigger,
  User,
} from '../../../models/user';
import { FLAGS, FlagsService } from '../../../services/flags.service';
import { ForegroundActivityNotificationsService } from '../../../services/foreground-activity-notifications.service';
import { UpgradePlanEvaluationType } from '../../../services/paywall-indicator.service';
import { SettingsService } from '../../../services/settings.service';
import { UserService } from '../../../services/user.service';
import { proFeatureSubscriptionTypes } from '../../../models/payment';
import Capability = AccessControl.Capability;

type EmailAudienceSettingKey =
  | 'summaryEmailAudience'
  | 'coachingEmailAudience'
  | 'snippetsEmailAudience';

interface AiAssistSettingsValue {
  crEnableAISummarization: boolean;
  aiCoachingInsights: boolean;
  aiSnippets: boolean;
  summaryEmailAudience: EmailAudience[];
  coachingEmailAudience: EmailAudience[];
  snippetsEmailAudience: EmailAudience[];
  shareSnippetsSettings?: {
    trigger?: SnippetsNotificationAndVisibilityTrigger;
  };
}

interface EmailAudienceOption {
  label: string;
  value: EmailAudience;
}

interface snipptetsSettings {
  aiSnippets: boolean;
  triggerLabel: string;
}

interface EmailAudienceColumn {
  settingKey: EmailAudienceSettingKey;
  label: string;
}

interface EmailAudienceCellViewModel {
  audience: EmailAudience;
  settingKey: EmailAudienceSettingKey;
  columnLabel: string;
  isDisabled: boolean;
  isSelected: boolean;
}

interface EmailAudienceRowViewModel {
  option: EmailAudienceOption;
  cells: EmailAudienceCellViewModel[];
}

interface SnippetVisibilityOption {
  value: SnippetsNotificationAndVisibilityTrigger;
  label: string;
}

interface EmailAudienceTableViewModel {
  columns: EmailAudienceColumn[];
  rows: EmailAudienceRowViewModel[];
}

@UntilDestroy()
@Component({
  selector: 'app-user-settings-ai-assist',
  templateUrl: './user-settings-ai-assist.component.html',
  styleUrls: ['./user-settings-ai-assist.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UserSettingsAiAssistComponent implements OnInit {
  @Input() isMobile = false;
  @Input() PANELS: any;
  @Input() form!: UntypedFormGroup;
  @Output() goBack = new EventEmitter<any>();
  @Output() formChangedEvent: EventEmitter<boolean> = new EventEmitter();

  settingsForm?: UntypedFormGroup;
  user?: User;
  isLoading = false;

  public initialFormValue: unknown;
  public formChanged$ = new BehaviorSubject<boolean>(false);
  protected readonly IconTypes = IconTypes;

  readonly snippetVisibilityOptions: SnippetVisibilityOption[] = [
    {
      value: SnippetsNotificationAndVisibilityTrigger.AUTOMATICALLY,
      label: this.translateService.instant('Always show'),
    },
    {
      value: SnippetsNotificationAndVisibilityTrigger.WITH_APPROVAL,
      label: this.translateService.instant('Require approval'),
    },
    {
      value: SnippetsNotificationAndVisibilityTrigger.NEVER,
      label: this.translateService.instant('Never show'),
    },
  ];

  public canUseAISummarization$ = this.capabilityTierService.shouldEnableCapability$(
    Capability.CLOUD_RECORDING_AI_SUMMARIZATION,
  );
  public canUseLiveCallAlerts$ = this.capabilityTierService.shouldEnableCapability$(
    Capability.LIVE_CALL_ALERTS,
  );
  public canUseAICoach$ = this.capabilityTierService.shouldEnableCapability$(Capability.AI_COACH);
  public canUseAISafeguarding$ = this.capabilityTierService.shouldEnableCapability$(
    Capability.AI_SAFEGUARDING,
  );
  public canUseAISnippets$ = this.capabilityTierService.shouldEnableCapability$(
    Capability.AI_SNIPPETS,
  );
  inactivityNotificationsPaid = false;

  public readonly inactivityNotificationsEnabled$ = this.flagsService.featureFlagChanged(
    FLAGS.INACTIVITY_NOTIFICATIONS,
  );
  public readonly liveCallAlertsEnabled$ = this.flagsService.featureFlagChanged(
    FLAGS.LIVE_NOTIFICATION_SPEAKING_DURATION,
  );
  public readonly aiCoachingInsightsEnabled$ = this.flagsService.featureFlagChanged(
    FLAGS.AI_RECORDING_INSIGHT_DASHBOARD,
  );
  public readonly aiSafeguardingUiEnabled$ = this.flagsService.featureFlagChanged(
    FLAGS.AI_SAFEGUARDING_UI,
  );
  public readonly nudityDetectionEnabled$ = this.flagsService.featureFlagChanged(
    FLAGS.ENABLE_NSFW_DETECTION,
  );
  public readonly UpgradePlanEvaluationType = UpgradePlanEvaluationType;
  protected readonly proFeatureSubscriptionTypes = proFeatureSubscriptionTypes;

  readonly learnMoreUrls = {
    sessionSummaries:
      'https://intercom.help/pencil/en/articles/9179685-how-to-enable-and-view-ai-summaries',
    coachingInsights:
      'https://helpdesk.pencilspaces.com/en/articles/12996565-how-to-use-the-ai-coach-insights',
    safeguardingAlerts:
      'https://helpdesk.pencilspaces.com/en/articles/15960073-how-to-enable-safeguarding-alerts',
    aiSnippets: 'https://helpdesk.pencilspaces.com/en/articles/16543197-how-to-use-the-ai-snippet',
    nudityDetection:
      'https://intercom.help/pencil/en/articles/15005606-how-to-enable-nudity-detection-alerts',
  } as const;

  // Post-Session is the auto-selected tab.
  readonly defaultTabIndex = 0;

  readonly emailAudienceOptions: EmailAudienceOption[] = [
    {
      label: this.translateService.instant('Hosts (in session)'),
      value: EmailAudience.HOSTS_IN_SESSION,
    },
    {
      label: this.translateService.instant('Hosts (not in session)'),
      value: EmailAudience.HOSTS_NOT_IN_SESSION,
    },
    {
      label: this.translateService.instant('Participants (in session)'),
      value: EmailAudience.PARTICIPANTS_IN_SESSION,
    },
    {
      label: this.translateService.instant('Participants (not in session)'),
      value: EmailAudience.PARTICIPANTS_NOT_IN_SESSION,
    },
  ];

  emailAudienceTable$!: Observable<EmailAudienceTableViewModel>;
  snippetsSettings$!: Observable<snipptetsSettings>;

  // Coaching emails are only relevant for hosts.
  private static readonly COACHING_INELIGIBLE_AUDIENCES: ReadonlySet<EmailAudience> = new Set([
    EmailAudience.PARTICIPANTS_IN_SESSION,
    EmailAudience.PARTICIPANTS_NOT_IN_SESSION,
  ]);

  constructor(
    private userService: UserService,
    private settingsService: SettingsService,
    private translateService: TranslateService,
    private flagsService: FlagsService,
    public foregroundInactivityNotificationsService: ForegroundActivityNotificationsService,
    protected featureManagementService: FeatureManagementService,
    private capabilityTierService: CapabilityTierService,
  ) {}

  ngOnInit(): void {
    this.settingsForm = this.form?.controls.settings as UntypedFormGroup;

    this.userService.user.pipe(untilDestroyed(this)).subscribe((res) => {
      this.user = res?.user;
      this.inactivityNotificationsPaid =
        this.foregroundInactivityNotificationsService.userPaidForFeature(this.user);
      this.initForm();
    });

    this.emailAudienceTable$ = this.settingsForm.valueChanges.pipe(
      startWith(this.settingsForm.getRawValue() as AiAssistSettingsValue),
      map((settings: AiAssistSettingsValue) => this.buildEmailAudienceTable(settings)),
      shareReplay({ bufferSize: 1, refCount: true }),
    );

    this.snippetsSettings$ = this.settingsForm.valueChanges.pipe(
      startWith(this.settingsForm.getRawValue() as AiAssistSettingsValue),
      map((settings: AiAssistSettingsValue) => this.buildSnippetsSettings(settings)),
      shareReplay({ bufferSize: 1, refCount: true }),
    );

    // Subscribe to form changes once, not on every user emission, to avoid leaking subscriptions.
    this.form.valueChanges
      .pipe(
        map(() => !isEqual(this.initialFormValue, this.form.value)),
        tap((x) => this.formChangedEvent.emit(x)),
        untilDestroyed(this),
      )
      .subscribe(this.formChanged$);
  }

  initForm(): void {
    this.form.patchValue({
      ...this.user?.institution,
      settings: {
        ...(this.user?.institution?.settings ?? {}),
        // display it as off when it is not paid
        inactivityNotifications:
          this.inactivityNotificationsPaid &&
          (this.user?.institution?.settings?.inactivityNotifications ?? false),
        aiCoachingInsights: this.user?.institution?.settings?.aiCoachingInsights ?? false,
        aiSafeguarding: this.user?.institution?.settings?.aiSafeguarding ?? false,
        aiSnippets: this.user?.institution?.settings?.aiSnippets ?? false,
        shareSnippetsSettings: {
          ...(this.user?.institution?.settings?.shareSnippetsSettings ?? {}),
          trigger:
            this.user?.institution?.settings?.shareSnippetsSettings?.trigger ??
            DEFAULT_SHARE_SNIPPETS_TRIGGER,
        },
        enableNudityDetectionAlerts:
          this.user?.institution.settings?.enableNudityDetectionAlerts ?? false,
        summaryEmailAudience: this.user?.institution.settings?.summaryEmailAudience ?? [
          EmailAudience.HOSTS_IN_SESSION,
          EmailAudience.HOSTS_NOT_IN_SESSION,
        ],
        coachingEmailAudience: this.user?.institution.settings?.coachingEmailAudience ?? [
          EmailAudience.HOSTS_IN_SESSION,
          EmailAudience.HOSTS_NOT_IN_SESSION,
        ],
        snippetsEmailAudience: this.user?.institution.settings?.snippetsEmailAudience ?? [
          EmailAudience.HOSTS_IN_SESSION,
          EmailAudience.HOSTS_NOT_IN_SESSION,
        ],
      },
    });

    this.initialFormValue = this.form.value;
  }

  setSnippetVisibility(trigger: SnippetsNotificationAndVisibilityTrigger): void {
    this.settingsForm?.get('shareSnippetsSettings.trigger')?.setValue(trigger);
  }

  private buildSnippetsSettings(settings: AiAssistSettingsValue): snipptetsSettings {
    return {
      aiSnippets: settings.aiSnippets,
      triggerLabel:
        this.snippetVisibilityOptions.find(
          (option) => option.value === settings.shareSnippetsSettings?.trigger,
        )?.label ?? this.translateService.instant('Require approval'),
    };
  }

  private buildEmailAudienceTable(settings: AiAssistSettingsValue): EmailAudienceTableViewModel {
    const columns = this.getVisibleEmailAudienceColumns(settings);

    return {
      columns,
      rows: this.emailAudienceOptions.map((option) => ({
        option,
        cells: columns.map((column) => ({
          audience: option.value,
          settingKey: column.settingKey,
          columnLabel: column.label,
          isDisabled: this.isEmailAudienceDisabled(option.value, column.settingKey),
          isSelected: settings[column.settingKey].includes(option.value),
        })),
      })),
    };
  }

  private getVisibleEmailAudienceColumns(settings: AiAssistSettingsValue): EmailAudienceColumn[] {
    const columns: EmailAudienceColumn[] = [];

    if (settings.crEnableAISummarization) {
      columns.push({
        settingKey: 'summaryEmailAudience',
        label: this.translateService.instant('Summary'),
      });
    }
    if (settings.aiCoachingInsights) {
      columns.push({
        settingKey: 'coachingEmailAudience',
        label: this.translateService.instant('Coaching'),
      });
    }
    if (settings.aiSnippets) {
      columns.push({
        settingKey: 'snippetsEmailAudience',
        label: this.translateService.instant('Snippets'),
      });
    }

    return columns;
  }

  private isEmailAudienceDisabled(
    audience: EmailAudience,
    settingKey: EmailAudienceSettingKey,
  ): boolean {
    if (settingKey !== 'coachingEmailAudience') {
      return false;
    }
    return UserSettingsAiAssistComponent.COACHING_INELIGIBLE_AUDIENCES.has(audience);
  }

  onEmailAudienceSelectionChange(
    isSelected: boolean,
    audience: EmailAudience,
    settingKey: EmailAudienceSettingKey,
  ): void {
    const control = this.settingsForm?.get(settingKey);
    if (!control) {
      return;
    }
    const current: EmailAudience[] = control.value ?? [];
    const updated = isSelected
      ? Array.from(new Set([...current, audience]))
      : current.filter((currentAudience) => currentAudience !== audience);
    control.setValue(updated);
  }

  trackByEmailAudienceColumn(_: number, column: EmailAudienceColumn): string {
    return column.settingKey;
  }

  trackByEmailAudienceRow(_: number, row: EmailAudienceRowViewModel): string {
    return row.option.value;
  }

  trackByEmailAudienceCell(_: number, cell: EmailAudienceCellViewModel): string {
    return cell.settingKey;
  }

  submitChanges(): void {
    if (!this.user || this.isLoading) {
      return;
    }

    this.isLoading = true;
    const updatedUser = {
      ...this.user,
      institution: {
        ...this.user?.institution,
        ...this.form.value,
      },
    };

    updatedUser.institution.settings = {
      ...this.user?.institution.settings,
      ...this.form.value.settings,
    };

    // if the option was turned off because it wasn't paid, we don't want to change its value.
    // because this will create a bug where if the user is not subscribed and changed something in the settings
    // the inactivityNotifications options will be turned off.
    if (
      !this.inactivityNotificationsPaid &&
      this.user?.institution?.settings?.inactivityNotifications
    ) {
      updatedUser.institution.settings.inactivityNotifications =
        this.user?.institution.settings.inactivityNotifications;
    }

    this.userService
      .updateInstitution(updatedUser)
      .pipe(take(1))
      .subscribe({
        next: () => {
          this.refresh(updatedUser);
          if (this.isMobile) {
            this.goBack.emit();
          }
        },
        error: () => {
          this.settingsService.showToast(
            {
              title: this.translateService.instant('Update failed'),
              description: this.translateService.instant('An error occurred. Please try again.'),
            },
            true,
          );
        },
        complete: () => {
          this.isLoading = false;
          this.formChanged$.next(false);
          this.formChangedEvent.emit(false);
        },
      });
  }

  refresh(userPayload: User): void {
    const userFromService = this.userService.user.getValue();
    this.userService.user.next({
      ...userFromService,
      user: userPayload,
    });
    this.settingsService.showToast({
      title: this.translateService.instant('Institution updated'),
    });
  }
}
